import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Server, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SetupAccount() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUserFromInvite } = useAuth();
  const [state, setState] = useState("checking"); // checking | valid | invalid
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      setErr("No invite token provided.");
      return;
    }
    api
      .get(`/auth/invite/${token}`)
      .then((r) => {
        setInvite(r.data);
        setState("valid");
      })
      .catch((e) => {
        setState("invalid");
        setErr(apiErr(e));
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password: form.password });
      localStorage.setItem("token", data.token);
      setUserFromInvite(data.user);
      toast.success("Account activated. Welcome!");
      navigate("/dashboard");
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0b1020] text-white p-12 relative overflow-hidden">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-primary grid place-items-center"><Server className="h-5 w-5" /></div>
          <span className="font-display font-extrabold text-lg tracking-tight">HMG Linux Lab</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display font-black text-4xl xl:text-5xl leading-[1.05] tracking-tight">You've been invited.</h1>
          <p className="mt-5 text-white/70 text-base leading-relaxed">Set a password to activate your Linux Live Lab account and start hands-on training.</p>
        </div>
        <div className="text-white/40 text-xs relative z-10">Development sandbox • Isolated per session</div>
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          {state === "checking" && (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Validating your invite…</div>
          )}

          {state === "invalid" && (
            <div className="text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 grid place-items-center mb-3"><AlertTriangle className="h-6 w-6 text-destructive" /></div>
              <h2 className="font-display font-bold text-xl">Invite unavailable</h2>
              <p className="text-muted-foreground text-sm mt-2">{err}</p>
              <Button variant="outline" className="mt-6" onClick={() => navigate("/login")}>Go to sign in</Button>
            </div>
          )}

          {state === "valid" && (
            <>
              <div className="flex items-center gap-2 text-success text-sm mb-2"><CheckCircle2 className="h-4 w-4" /> Invite verified</div>
              <h2 className="font-display font-bold text-2xl tracking-tight">Set up your account</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Welcome, <span className="font-medium text-foreground">{invite?.name}</span> · {invite?.email}
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="setup-form">
                <div className="space-y-1.5">
                  <Label htmlFor="password">Create password</Label>
                  <Input id="password" type="password" data-testid="setup-password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required placeholder="At least 6 characters" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input id="confirm" type="password" data-testid="setup-confirm" value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} required placeholder="Re-enter password" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="setup-submit">
                  {loading ? "Activating…" : "Activate account"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
